import { Badge } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { BodyText } from "@/components/ui/Text";
import {
  AiFeedItemStatus,
  isCreateConditionItem,
} from "@/features/AiFeed/network/types";
import type {
  AiFeedItem,
  CreateConditionIntent,
} from "@/features/AiFeed/network/types";
import {
  getChartedToLabel,
  getIntentIcon,
  getIntentLabel,
  getProducerLabel,
} from "@/features/AiFeed/presentation";
import { ErrorBoundary } from "@/lib/monitoring";
import { formatDateTime } from "@/lib/time";
import { Check, X } from "lucide-react";
import { ConditionCodePicker } from "./MedicalConditions/ConditionCodePicker";
import { useCreateConditionActions } from "./MedicalConditions/useCreateConditionActions";
import { UnknownItemCard } from "./UnknownItemCard";

interface AiFeedItemCardProps {
  item: AiFeedItem;
  encounterId: string;
}

type CreateConditionAiFeedItem = AiFeedItem & { intent: CreateConditionIntent };

interface AiFeedItemCardInnerProps {
  item: CreateConditionAiFeedItem;
  encounterId: string;
}

const STATUS_BADGE: Record<
  AiFeedItemStatus,
  {
    label: string;
    variant: "warning" | "info" | "success" | "default";
    textColor: "warning" | "info" | "success" | "secondary";
  }
> = {
  [AiFeedItemStatus.pending]: {
    label: "Needs Review",
    variant: "warning",
    textColor: "warning",
  },
  [AiFeedItemStatus.accepted]: {
    label: "Accepted",
    variant: "success",
    textColor: "success",
  },
  [AiFeedItemStatus.rejected]: {
    label: "Rejected",
    variant: "default",
    textColor: "secondary",
  },
  [AiFeedItemStatus.expired]: {
    label: "Expired",
    variant: "default",
    textColor: "secondary",
  },
};

/** Exhaustive status → past-tense verb for the resolved footer line. */
const RESOLVER_VERB: Record<AiFeedItemStatus, string> = {
  [AiFeedItemStatus.pending]: "",
  [AiFeedItemStatus.accepted]: "Accepted",
  [AiFeedItemStatus.rejected]: "Rejected",
  [AiFeedItemStatus.expired]: "Expired",
};

export function AiFeedItemCard({ item, encounterId }: AiFeedItemCardProps) {
  if (!isCreateConditionItem(item)) {
    return (
      <div className="rounded-lg border border-secondary">
        <UnknownItemCard item={item} />
      </div>
    );
  }
  // AiFeedItemCardInner must be a separate component: the unconditional
  // useCreateConditionActions hook inside it can't live alongside this
  // early-return branch without violating rules-of-hooks.
  return (
    <ErrorBoundary
      fallback={
        <div className="rounded-lg border border-secondary bg-tertiary p-3">
          <BodyText size="sm" textColor="error">
            Unable to display this item.
          </BodyText>
        </div>
      }
    >
      <AiFeedItemCardInner item={item} encounterId={encounterId} />
    </ErrorBoundary>
  );
}

function AiFeedItemCardInner({ item, encounterId }: AiFeedItemCardInnerProps) {
  const {
    suggestions,
    selectedCode,
    onSelect,
    handleAccept,
    handleReject,
    isAccepting,
    isRejecting,
    canAccept,
    isMutating,
  } = useCreateConditionActions(item, encounterId);

  const intent = item.intent;
  const ItemIcon = getIntentIcon(item.itemType);
  const intentLabel = getIntentLabel(item.itemType);
  const producerLabel = getProducerLabel(item.producer);
  const statusBadge = STATUS_BADGE[item.status];
  const isPending = item.status === AiFeedItemStatus.pending;
  const isDimmed =
    item.status === AiFeedItemStatus.rejected ||
    item.status === AiFeedItemStatus.expired;
  const chartedToLabel = getChartedToLabel(item.itemType);
  const proposedTime = formatDateTime(item.createdAt);
  const resolvedTime = item.resolvedAt ? formatDateTime(item.resolvedAt) : "";
  const resolvedByName = item.resolvedByName ?? "";
  const cardBg = isPending ? "bg-primary" : "bg-tertiary";
  const resolverVerb = RESOLVER_VERB[item.status];

  return (
    <div
      className={`flex rounded-lg border border-secondary overflow-hidden ${cardBg}${isDimmed ? " opacity-60" : ""}`}
    >
      {/* Full-height left strip: pale purple bg + icon centered vertically */}
      <div className="flex items-center justify-center shrink-0 px-2.5 bg-superclinical">
        <ItemIcon className="h-5 w-5 text-primary" aria-hidden />
      </div>

      {/* Right content column */}
      <div className="flex-1 min-w-0 p-3 space-y-2">
        {/* Title */}
        <BodyText size="sm" bold tag="p">
          {intentLabel}: {intent.extractedTerm}
        </BodyText>

        {/* Primary content: picker (pending) or nothing (resolved — term is in title) */}
        {isPending && (
          <ConditionCodePicker
            suggestions={suggestions}
            patientId={intent.patientId}
            selectedCode={selectedCode}
            onSelect={onSelect}
          />
        )}

        {/* Charted confirmation (accepted only) — rendered above the badge row */}
        {item.status === AiFeedItemStatus.accepted && (
          <div className="space-y-0.5">
            <div className="flex items-center gap-1">
              <Check className="h-3 w-3 text-success shrink-0" aria-hidden />
              <BodyText size="xs" textColor="success" tag="p">
                Charted to {chartedToLabel}
                {item.selectedCode ? " as:" : ""}
              </BodyText>
            </div>
            {item.selectedCode && (
              <BodyText size="sm" bold tag="p">
                {item.selectedCode.display}
              </BodyText>
            )}
          </div>
        )}

        {/* Badge row + text action buttons (no intent badge — term is in the title) */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            {item.status !== AiFeedItemStatus.accepted && (
              <Badge variant={statusBadge.variant}>
                <BodyText size="xs" textColor={statusBadge.textColor}>
                  {statusBadge.label}
                </BodyText>
              </Badge>
            )}
            <Badge variant="info">
              <BodyText size="xs" textColor="info">
                {producerLabel}
              </BodyText>
            </Badge>
          </div>
          {isPending && (
            <div className="flex items-center gap-1.5 shrink-0">
              <Button
                variant="primary"
                size="xs"
                leftIcon={Check}
                onClick={handleAccept}
                disabled={!canAccept}
                isLoading={isAccepting}
              >
                Accept
              </Button>
              <Button
                variant="secondary"
                size="xs"
                leftIcon={X}
                onClick={handleReject}
                disabled={isMutating}
                isLoading={isRejecting}
              >
                Reject
              </Button>
            </div>
          )}
        </div>

        {/* Resolved footer: resolver line + proposed time (tightly stacked) */}
        {!isPending && (
          <div className="space-y-0.5">
            <BodyText size="xs" textColor="tertiary" tag="p">
              {resolverVerb}
              {resolvedTime ? ` on ${resolvedTime}` : ""}
              {resolvedByName ? ` by ${resolvedByName}` : ""}
            </BodyText>
            <BodyText size="xs" textColor="tertiary" tag="p">
              Proposed {proposedTime}
            </BodyText>
          </div>
        )}

        {/* Proposed timestamp — pending only, at the bottom after the badge row */}
        {isPending && (
          <BodyText size="xs" textColor="tertiary" tag="p">
            Proposed {proposedTime}
          </BodyText>
        )}
      </div>
    </div>
  );
}
